import React from 'react';
import { Link } from 'react-router-dom';
import '../styles/LegalPages.css';

const TermsAndConditions = () => {
  return (
    <div className="legal-page">
      <div className="container">
        <div className="legal-header">
          <Link to="/" className="back-link">← Back to Home</Link>
          <h1>Terms and Conditions</h1>
          <p className="last-updated">Last Updated: December 2024</p>
        </div>

        <div className="legal-content">
          <section>
            <h2>1. Acceptance of Terms</h2>
            <p>By accessing and using Sahayak ("Platform", "Service", "We", "Us"), you accept and agree to be bound by these Terms and Conditions. If you do not agree to these terms, please do not use our platform.</p>
          </section>

          <section>
            <h2>2. Service Description</h2>
            <p>Sahayak is an online marketplace connecting customers with independent service providers ("Helpers") for various household services including but not limited to:</p>
            <ul>
              <li>Sweeping and mopping and maintenance</li>
              <li>Cooking and meal preparation</li>
              <li>Laundry and ironing services</li>
              <li>Gardening and plant care</li>
              <li>Pet care services</li>
              <li>Elderly care assistance</li>
              <li>Other household support services</li>
            </ul>
            <p><strong>IMPORTANT DISCLAIMER:</strong> Sahayak acts SOLELY as an intermediary technology platform. We do NOT provide services directly, employ helpers, or have any control over the quality, safety, or legality of services provided. All services are performed by independent contractors who are solely responsible for their actions, conduct, and service delivery.</p>
          </section>

          <section>
            <h2>3. User Eligibility</h2>
            <ul>
              <li>Users must be at least 18 years of age</li>
              <li>Users must provide accurate and current information</li>
              <li>Helpers must possess legal authorization to work in India</li>
              <li>Helpers must pass our verification and background check process</li>
              <li>Users must comply with all applicable local, state, and national laws</li>
            </ul>
          </section>

          <section>
            <h2>4. Account Registration</h2>
            <h3>Customer Accounts</h3>
            <ul>
              <li>Provide accurate personal and contact information</li>
              <li>Maintain confidentiality of account credentials</li>
              <li>Promptly update account information when changes occur</li>
              <li>Be responsible for all activities under your account</li>
            </ul>

            <h3>Helper Accounts</h3>
            <ul>
              <li>Complete identity verification process</li>
              <li>Provide valid government-issued identification</li>
              <li>Submit relevant skill certifications (if applicable)</li>
              <li>Maintain updated availability and service information</li>
              <li>Comply with helper conduct standards</li>
            </ul>
          </section>

          <section>
            <h2>5. Booking and Services</h2>
            <h3>Service Requests</h3>
            <ul>
              <li>Customers may request services through the platform</li>
              <li>Service requests must include accurate details and requirements</li>
              <li>Helpers may accept or decline service requests</li>
              <li>Confirmed bookings create a service agreement between customer and helper</li>
            </ul>

            <h3>Service Standards</h3>
            <ul>
              <li>Helpers must arrive on time and provide agreed services professionally</li>
              <li>Customers must provide safe working environment and clear instructions</li>
              <li>Both parties must treat each other with respect and professionalism</li>
              <li>Any changes to service scope must be mutually agreed upon</li>
            </ul>
          </section>

          <section>
            <h2>6. Pricing and Payments</h2>
            <h3>Pricing Structure</h3>
            <ul>
              <li>Service prices are set by individual helpers</li>
              <li>Platform fee of 5% applies to all bookings</li> 
              <li>Payment processing fee of 2% applies to online payments</li>
              <li>All prices are displayed inclusive of applicable taxes</li>
            </ul>

            <h3>Payment Methods</h3>
            <ul>
              <li><strong>Cash Payment:</strong> Direct payment to helper after service completion</li>
              <li><strong>Online Payment:</strong> Secure payment through Razorpay (UPI, Cards, Wallets, Net Banking)</li>
            </ul>

            <h3>Payment Terms</h3>
            <ul>
              <li>Online payments are processed immediately upon booking confirmation</li>
              <li>Cash payments are due upon service completion</li>
              <li>Helper payments are released after successful service completion</li>
              <li>Platform fees are non-refundable except in cases of platform error</li>
            </ul>
          </section>

          <section>
            <h2>7. Cancellation Policy</h2>
            <h3>Customer Cancellations</h3>
            <ul>
              <li><strong>Free Cancellation:</strong> Up to 2 hours before scheduled service time</li>
              <li><strong>Late Cancellation:</strong> Within 2 hours of service - 25% cancellation fee</li>
              <li><strong>No-Show:</strong> Full service charge applies</li>
            </ul>

            <h3>Helper Cancellations</h3>
            <ul>
              <li>Helpers may cancel up to 4 hours before service time without penalty</li>
              <li>Late cancellations may result in account warnings or suspension</li>
              <li>Emergency cancellations will be reviewed case-by-case</li>
            </ul>
          </section>

          <section>
            <h2>8. User Conduct</h2>
            <h3>Prohibited Activities</h3>
            <ul>
              <li>Harassment, discrimination, or inappropriate behavior</li>
              <li>Providing false information or fraudulent documents</li>
              <li>Circumventing platform for direct transactions</li>
              <li>Damaging property or stealing</li>
              <li>Violating any applicable laws or regulations</li>
              <li>Creating multiple accounts or fake profiles</li>
              <li>Interfering with platform operations or security</li>
            </ul>

            <h3>Consequences</h3>
            <p>Violations may result in account warnings, suspension, or permanent termination.</p>
          </section>

          <section>
            <h2>9. Safety and Insurance</h2>
            <p><strong>USER RESPONSIBILITY FOR SAFETY:</strong></p>
            <ul>
              <li>Sahayak conducts basic background verification but makes NO GUARANTEES about helper reliability, honesty, or competence</li>
              <li>Users are SOLELY RESPONSIBLE for their own safety, security, and property during all service interactions</li>
              <li>Users must exercise their own judgment and take appropriate precautions when allowing helpers into their premises</li>
              <li>Sahayak is NOT RESPONSIBLE for any theft, property damage, personal injury, or criminal activity</li>
              <li>Users are advised to secure valuables and supervise all service activities</li>
              <li>Any insurance coverage is between helpers and their insurance providers - Sahayak provides NO insurance coverage</li>
              <li>Users engage helpers entirely AT THEIR OWN RISK</li>
            </ul>
          </section>

          <section>
            <h2>10. Intellectual Property</h2>
            <ul>
              <li>Sahayak platform, logo, and content are our intellectual property</li>
              <li>Users may not reproduce, distribute, or modify our content</li>
              <li>User-generated content remains user's property but grants us usage rights</li>
              <li>Respect third-party intellectual property rights</li>
            </ul>
          </section>

          <section>
            <h2>11. Limitation of Liability</h2>
            <p><strong>MAXIMUM LEGAL PROTECTION:</strong></p>
            <ul>
              <li>Sahayak's total liability for ANY claim is strictly limited to the platform fees paid for the specific transaction</li>
              <li>Sahayak is NOT LIABLE for any direct, indirect, incidental, consequential, or punitive damages</li>
              <li>We are NOT RESPONSIBLE for helper performance, service quality, punctuality, or professional conduct</li>
              <li>Sahayak is NOT LIABLE for any property damage, theft, loss, personal injury, or death</li>
              <li>We are NOT RESPONSIBLE for any financial losses, business interruption, or emotional distress</li>
              <li>Users acknowledge that they engage with helpers entirely at their own risk and expense</li>
              <li>This limitation applies regardless of the legal theory (contract, tort, negligence, etc.)</li>
              <li>Some jurisdictions may not allow liability limitations - in such cases, our liability is limited to the maximum extent permitted by law</li>
            </ul>
          </section>

          <section>
            <h2>12. User Indemnification</h2>
            <p><strong>USER AGREES TO PROTECT SAHAYAK:</strong></p>
            <ul>
              <li>Users agree to indemnify, defend, and hold harmless Sahayak from any claims, damages, losses, or expenses</li>
              <li>This includes legal fees arising from user's use of the platform or interactions with helpers</li>
              <li>Users are responsible for any damages caused by their breach of these terms</li>
              <li>Users must compensate Sahayak for any costs incurred due to user misconduct or negligence</li>
              <li>This indemnification survives termination of user accounts</li>
            </ul>
          </section>

          <section>
            <h2>13. Dispute Resolution</h2>
            <ul>
              <li>ALL disputes must first be reported to Sahayak support for mediation attempts</li>
              <li>Users waive the right to participate in class action lawsuits against Sahayak</li>
              <li>Any legal disputes will be resolved through binding arbitration in India under Indian law</li>
              <li>Users consent to jurisdiction of Indian courts for any legal proceedings</li>
              <li>Sahayak reserves the right to suspend accounts during dispute investigations</li>
              <li>Users acknowledge that Sahayak's role is limited to platform facilitation only</li>
            </ul>
          </section>

          <section>
            <h2>14. Platform Modifications and Termination</h2>
            <ul>
              <li>Sahayak may modify, suspend, or discontinue services at any time without notice</li>
              <li>We may terminate user accounts immediately for any terms violations</li>
              <li>Users have no right to continued access to the platform</li>
              <li>Terms may be updated with notice - continued use constitutes acceptance</li>
              <li>Sahayak is not liable for any losses due to service modifications or terminations</li>
            </ul>
          </section>

          <section>
            <h2>15. Account Termination</h2>
            <h3>By Users</h3>
            <ul>
              <li>Users may terminate accounts but remain liable for outstanding obligations</li>
              <li>Termination does not waive user's indemnification responsibilities</li>
              <li>Data deletion follows privacy policy but may be retained for legal compliance</li>
            </ul>

            <h3>By Sahayak</h3>
            <ul>
              <li>We may terminate accounts immediately for any reason without prior notice</li>
              <li>Termination may occur for safety concerns, terms violations, or suspicious activity</li>
              <li>Users are not entitled to refunds upon account termination for violations</li>
            </ul>
          </section>

          <section>
            <h2>16. Force Majeure</h2>
            <p>Sahayak is not liable for any delays or failures due to circumstances beyond our control, including but not limited to natural disasters, government actions, network failures, or global pandemics.</p>
          </section>

          <section>
            <h2>17. Contact Information</h2>
            <p>For questions about these terms, contact us at:</p>
            <div className="contact-info">
              <p><strong>Email:</strong> sahayak.platform@gmail.com</p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsAndConditions; 